import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext({
  user: null,
  firm: null,
  firms: [],
  role: "viewer",
  isSuperAdmin: false,
  loading: true,
  refresh: async () => {},
  logout: async () => {},
  exchangeSession: async () => {},
  switchFirm: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [firms, setFirms] = useState([]);
  const [role, setRole] = useState("viewer");
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const apply = (data) => {
    setUser(data?.user || null);
    setFirms(data?.firms || []);
    setRole(data?.role || "viewer");
    setIsSuperAdmin(!!data?.is_super_admin);
  };

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      apply(r.data);
    } catch {
      apply({ user: null, firms: [], role: "viewer", is_super_admin: false });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: when returning from Emergent OAuth callback the URL has #session_id=...;
    // skip the /auth/me probe and let AuthCallback exchange the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const exchangeSession = useCallback(async (sessionId) => {
    const r = await api.post("/auth/session", { session_id: sessionId });
    apply(r.data);
    return r.data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Session may already be invalid or backend unreachable. Clear local state regardless.
      console.warn("[auth] logout request failed:", err);
    }
    apply({ user: null, firms: [], role: "viewer", is_super_admin: false });
  }, []);

  const switchFirm = useCallback(async (firmId) => {
    const r = await api.post("/auth/switch-firm", { firm_id: firmId });
    apply(r.data);
    return r.data;
  }, []);

  const currentFirm = useMemo(
    () => (firms || []).find((f) => f.firm_id === user?.current_firm_id) || null,
    [firms, user?.current_firm_id],
  );

  // Memoize context value so consumers don't re-render when AuthProvider's
  // parent re-renders for unrelated reasons. Only changes when one of the
  // identity/permission fields actually changes.
  const value = useMemo(
    () => ({ user, firm: currentFirm, firms, role, isSuperAdmin, loading, refresh, exchangeSession, logout, switchFirm }),
    [user, currentFirm, firms, role, isSuperAdmin, loading, refresh, exchangeSession, logout, switchFirm],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
